import * as React from 'react';
import './AppointmentsTable.scss';
import styled from 'styled-components';
import { Translate, useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { CSVLink } from 'react-csv';
import StyledTable, { PrimaryText, SecondaryText } from '../StyledTable';
import ColorBall from '../ColorBall';
import { AppointmentData, Payment, PaymentStatus, PermissionEnum } from '../../models';
import classnames from 'classnames';
import EuroIcon from '@material-ui/icons/Euro';
import DoneIcon from '@material-ui/icons/DoneOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import PhonelinkEraseIcon from '@material-ui/icons/PhonelinkErase';
import DeviceUnknownIcon from '@material-ui/icons/DeviceUnknown';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import PrintIcon from '@material-ui/icons/Print';
import moment from 'moment';
import ToolTip from '../ToolTip';
import { isPaid } from '../../shared/models-helpers/appointmentDataHelper';
import { formatEuros, shortenName } from '../../shared/string';
import { getAppointmentTotalPayments, getTotalPrice, serviceName } from '../../shared/appointmint/appointment';
import { getAppointmentConfirmationStatus } from '../../shared/appointmint/appointmentConfirmationStatus';
import { useGeneralSettings } from '../../store/settings/hooks';
import { getClientFileNumberLabel } from '../../shared/clientSearch';
import SettingsIcon from "@material-ui/icons/Settings";
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { useCookies } from 'react-cookie';
import { CheckInputField } from '../Forms/fields/CheckInputField';
import Typography from '../Typography';
import { cookiesOptions, minutesToTimeString } from '../../shared/utils';
import { PaymentLabel } from '../Forms/fields/PaymentsList';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { getPermissions } from '../../store/session/selectors';
import WarningIcon from '@material-ui/icons/Warning';
import { AppointmentConfirmationEditableStatus } from '../../shared/appointmint/appointmentConfirmationStatus';
import AppointmentConfirmationStatusMenu from '../Appointments/AppointmentConfirmationStatusMenu';
import AppointmentContextMenu from '../Appointments/AppointmentContextMenu';
import AppButton from '../AppButton';
import { usePageWidth } from '../../app/PageWidthContext';

const StyledAppointmentsTable = styled(StyledTable)`
    && {
        tbody td {
            white-space: nowrap;
        }
    }
`;

export interface AppointmentsTableStateProps {
    appointments: AppointmentData[],
    isLoading?: boolean,
    className?: string,
    canEdit?: boolean,
    showExportCsv?: boolean,
    settings?: {
        hideDay?: boolean,
        hideColumns?: ColumnName[],
    }
}
export interface AppointmentsTableDispatchProps {
    onLoadMore?: (offset: number) => void,
    onCreateAppointment?: () => void,
    onEditAppointment?: (appointment: AppointmentData) => void,
    onEditPayment?: (appointment: AppointmentData) => void,
    onQuickPayment?: (appointment: AppointmentData, paymentType: string) => void,
    onMarkAppointmentDone?: (appointment: AppointmentData, hasHappened: boolean | undefined) => void,
    onSetAppointmentConfirmationStatus?: (appointment: AppointmentData, status: AppointmentConfirmationEditableStatus) => void,
    onOpenClient?: (clientId: string) => void,
    onOpenPack?: (packId: string) => void,
    onCreateInvoice?: (appointment: AppointmentData) => void,
    onDeleteAppointment?: (appointment: AppointmentData) => void,
    onSendSurvey?: (appointment: AppointmentData) => void,
    onRowContextMenu?: (appointment: AppointmentData, event: React.MouseEvent) => void,
}

export type AppointmentsTableProps = AppointmentsTableStateProps & AppointmentsTableDispatchProps;

type ColumnName = 'dateTime'|'confirmation'|'client'|'professional'|'room'|'appointmentType'|'price'|'effectivePrice'|'extras'|'actionButtons'|'pack'|'notes'|'payment'
const defaultColumns: ColumnName[] = ['dateTime', 'client', 'professional', 'room', 'appointmentType', 'price', 'actionButtons']
const allColumns: ColumnName[] = ['dateTime', 'confirmation', 'client', 'professional', 'room', 'appointmentType', 'extras', 'pack', 'price', 'effectivePrice', 'payment', 'actionButtons', 'notes']

export const formatPayment = (t: Translate<TK>, data: AppointmentData): React.ReactElement => {
    const totalPrice = getTotalPrice(data)
    const totalPaid = getAppointmentTotalPayments(data)
    const missingPayment = totalPrice > totalPaid
    const paymentTypesUsed = [...new Set(data.payments?.filter(p => p.status === PaymentStatus.Manual).map(p => p.type) ?? [])]
    const autoPayments = data.payments?.filter(p => p.status === PaymentStatus.Success) ?? []
    return (
        <span>
            {
                data.packId ? <span style={{color: 'green'}}><b>{t(TK.pack)}/{t(TK.subscription)}</b></span>
                    : missingPayment ? <span style={{color: 'red'}}><b>{t(TK.notPaid)}</b></span>
                    : <span style={{color: 'green'}}><b>{t(TK.paid)}</b></span>
            }
            &nbsp;
            {
                !!paymentTypesUsed.length && <span>({paymentTypesUsed.join(', ')})</span>
            }
            {
                !!autoPayments.length && <PaymentLabel paymentType={autoPayments[0].type} />
            }
        </span>
    )
}

export const formatPackInfo = (a: AppointmentData): string => {
    const packName = a.packName ?? '';
    if (a.packAppointmentPosition != null && a.packTotalCredits != null) return `(${a.packAppointmentPosition}/${a.packTotalCredits}) ${packName}`
    if (a.packAppointmentPosition != null && a.packTotalMinutes != null) return `(${a.packAppointmentPosition}/${minutesToTimeString(a.packTotalMinutes)}) ${packName}`
    return a.packName ?? ''
}

const buildStartDateTime = (appointment: AppointmentData): Date | null => {
    if (!appointment.date) return null;
    const date = new Date(appointment.date);
    if (Number.isNaN(date.getTime())) return null;

    if (appointment.start) {
        const [hours, minutes] = appointment.start.split(':').map(v => parseInt(v, 10));
        date.setHours(hours || 0, minutes || 0, 0);
    }

    return date;
}

const getConfirmationRequestSentAt = (appointment: AppointmentData): Date | null => {
    const startDate = buildStartDateTime(appointment);
    if (!startDate) return null;

    const requestHours = [
        ...(appointment.confirmationRequestsSentBySms ?? []),
        ...(appointment.confirmationRequestsSentByWhatsapp ?? []),
    ];
    if (!requestHours.length) return null;

    const latestHoursBefore = Math.min(...requestHours);
    const sentAt = new Date(startDate.getTime() - latestHoursBefore * 60 * 60 * 1000);
    return sentAt;
}

const formatDateTime = (value?: string | Date | null): string => {
    if (!value) return '';
    return moment(value).format('DD/MM/YYYY HH:mm');
}

const AppointmentsTable: React.FC<AppointmentsTableProps> = (props) => {

    const {
        appointments,
        className,
        isLoading,
        settings,
        canEdit,
        showExportCsv,
        onLoadMore,
        onCreateAppointment,
        onEditAppointment,
        onEditPayment,
        onQuickPayment,
        onMarkAppointmentDone,
        onSetAppointmentConfirmationStatus,
        onOpenClient,
        onOpenPack,
        onCreateInvoice,
        onDeleteAppointment,
        onSendSurvey,
        onRowContextMenu,
    } = props;

    const t = useTranslations();
    const pageWidth = usePageWidth();

    const [cookies, setCookie] = useCookies()
    const columnsCookie = cookies['APPOINTMENTS_TABLE_COLUMNS']
    const confirmationFeatureCookie = cookies['APPOINTMENTS_TABLE_CONFIRMATION_FEATURE']

    const [count, setCount] = React.useState(0);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [hasMoreToLoad, setHasMoreToLoad] = React.useState(true);
    const [columnsModalOpen, setColumnsModalOpen] = React.useState(false)
    const [paymentMenuState, setPaymentMenuState] = React.useState<{
        anchorEl: HTMLElement,
        appointment: AppointmentData,
    } | null>(null)
    const [confirmationMenuState, setConfirmationMenuState] = React.useState<{
        anchorEl: HTMLElement,
        appointment: AppointmentData,
    } | null>(null)
    const [rowContextMenu, setRowContextMenu] = React.useState<{
        mouseX: number,
        mouseY: number,
        appointment: AppointmentData,
    } | null>(null)


    const [columns, _setColumns] = React.useState<Record<ColumnName, boolean>>(() => {
        const enabledColumns = typeof columnsCookie === 'string'
            ? columnsCookie.split(',')
            : defaultColumns;
        return allColumns.reduce((acc, column) => ({
            ...acc,
            [column]: enabledColumns.includes(column),
        }), {} as Record<ColumnName, boolean>);
    })

    const setColumns = (value: Record<ColumnName, boolean> | ((prev: Record<ColumnName, boolean>) => Record<ColumnName, boolean>)) => {
        _setColumns(prev => {
            const next = typeof value === 'function' ? value(prev) : value;
            setCookie(
                'APPOINTMENTS_TABLE_COLUMNS',
                Object.entries(next).filter(([_, v]) => v).map(([k]) => k).join(','),
                cookiesOptions,
            )
            return next;
        })
    }

    const generalSettings = useGeneralSettings()
    const useClientFileNumber = generalSettings.useClientFileNumber === true
    const confirmationFeatureAvailable = useSelector(
        (state: ApplicationState) => (state.settings.reminders?.appointmentConfirmation?.channels?.length ?? 0) > 0
    )
    const canConfirmAppointments = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotAllowToConfirmAppointments))
    const canShowPrices = useSelector((state: ApplicationState) => !getPermissions(state).includes(PermissionEnum.DoNotShowPrices))
    const clientsById = useSelector((state: ApplicationState) => state.entities.clients.items)
    const previousConfirmationFeatureAvailable = React.useRef<boolean | undefined>(
        typeof confirmationFeatureCookie === 'string'
            ? confirmationFeatureCookie === 'true'
            : undefined
    )

    React.useEffect(() => {
        if (previousConfirmationFeatureAvailable.current === confirmationFeatureAvailable) {
            return;
        }

        setColumns(prev => {
            if (confirmationFeatureAvailable && !prev.confirmation) {
                return { ...prev, confirmation: true };
            }
            if (!confirmationFeatureAvailable && prev.confirmation) {
                return { ...prev, confirmation: false };
            }
            return prev;
        });

        setCookie(
            'APPOINTMENTS_TABLE_CONFIRMATION_FEATURE',
            confirmationFeatureAvailable ? 'true' : 'false',
            cookiesOptions
        );
        previousConfirmationFeatureAvailable.current = confirmationFeatureAvailable;
    }, [confirmationFeatureAvailable])

    React.useEffect(() => {
        if(isLoadingMore && appointments.length === count){
            setHasMoreToLoad(false);
        }
        setCount(appointments.length);
        // eslint-disable-next-line
    }, [appointments])

    React.useEffect(() => setIsLoadingMore(isLoading || false), [isLoading]);

    const handlePrint = React.useCallback(() => {
        const esc = (s: string | null | undefined) =>
            (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const companyName = esc(generalSettings.companyName);
        const logoHtml = generalSettings.companyImageUrl
            ? `<img src="${esc(generalSettings.companyImageUrl)}" alt="${companyName}" class="logo" />`
            : '';

        const rows = appointments.map(a => `
            <tr>
                <td>${esc(settings?.hideDay ? a.start : `${a.date} ${a.start}`)}</td>
                <td>${esc(a.clientName)}</td>
                <td>${esc(a.professionalName)}</td>
                <td>${esc(serviceName(a.type, generalSettings))}</td>
            </tr>`).join('');

        const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<base href="${window.location.origin}/">
<title>${companyName}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11pt;color:#222;background:#fff;padding:32px}
.header{display:flex;align-items:center;gap:16px;margin-bottom:28px;padding-bottom:16px;border-bottom:2px solid #00b07f}
.logo{max-height:56px;max-width:160px;object-fit:contain}
.company-name{font-size:20pt;font-weight:bold;color:#00b07f}
.print-date{margin-left:auto;font-size:9pt;color:#888}
table{width:100%;border-collapse:collapse;margin-top:8px}
thead tr{background:#00b07f;color:#fff}
th{padding:8px 12px;text-align:left;font-weight:600;font-size:10pt}
td{padding:7px 12px;font-size:10pt;border-bottom:1px solid #e8e8e8}
tbody tr:nth-child(even){background:#f7fdf9}
</style></head><body>
<div class="header">
  ${logoHtml}
  <span class="company-name">${companyName}</span>
  <span class="print-date">${new Date().toLocaleDateString()}</span>
</div>
<table><thead><tr>
<th>${esc(t(settings?.hideDay ? TK.time : TK.dateTime))}</th>
<th>${esc(t(TK.client))}</th>
<th>${esc(t(TK.professional))}</th>
<th>${esc(t(TK.appointmentType))}</th>
</tr></thead><tbody>${rows}</tbody></table>
</body></html>`;

        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url);
        if (!printWindow) { URL.revokeObjectURL(url); return; }
        printWindow.addEventListener('load', () => printWindow.print());
        printWindow.addEventListener('afterprint', () => {
            printWindow.close();
            URL.revokeObjectURL(url);
        });
    }, [appointments, generalSettings, settings, t])

    const handleOpenAppointment = React.useCallback((appointment: AppointmentData) => () => onEditAppointment && onEditAppointment(appointment), [onEditAppointment])
    const handleMarkAsDone = React.useCallback((appointment: AppointmentData) => () => {
        if (!onMarkAppointmentDone) return;
        const next = appointment.hasHappened === undefined ? true : appointment.hasHappened === true ? false : undefined;
        onMarkAppointmentDone(appointment, next);
    }, [onMarkAppointmentDone])
    const handlePaymentButtonClick = React.useCallback((appointment: AppointmentData) => (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onQuickPayment) {
            event.stopPropagation()
            setPaymentMenuState({ anchorEl: event.currentTarget, appointment })
            return
        }

        onEditPayment && onEditPayment(appointment)
    }, [onEditPayment, onQuickPayment])
    const closePaymentMenu = React.useCallback(() => {
        setPaymentMenuState(null)
    }, [])
    const handleQuickPaymentClick = React.useCallback((paymentType: string) => {
        if (paymentMenuState?.appointment && onQuickPayment) {
            onQuickPayment(paymentMenuState.appointment, paymentType)
        }
        setPaymentMenuState(null)
    }, [onQuickPayment, paymentMenuState])
    const handleOpenConfirmationMenu = React.useCallback((appointment: AppointmentData) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setConfirmationMenuState({ anchorEl: event.currentTarget, appointment })
    }, [])
    const handleCloseConfirmationMenu = React.useCallback(() => {
        setConfirmationMenuState(null)
    }, [])
    const handleSetAppointmentConfirmationStatus = React.useCallback((status: AppointmentConfirmationEditableStatus) => {
        if (confirmationMenuState?.appointment && onSetAppointmentConfirmationStatus) {
            onSetAppointmentConfirmationStatus(confirmationMenuState.appointment, status)
        }
        setConfirmationMenuState(null)
    }, [confirmationMenuState, onSetAppointmentConfirmationStatus])
    const handleAdvancedPaymentClick = React.useCallback(() => {
        if (paymentMenuState?.appointment && onEditPayment) {
            onEditPayment(paymentMenuState.appointment)
        }
        setPaymentMenuState(null)
    }, [onEditPayment, paymentMenuState])
    const handleRowContextMenu = React.useCallback((appointment: AppointmentData) => (event: React.MouseEvent) => {
        event.preventDefault()
        if (onRowContextMenu) {
            onRowContextMenu(appointment, event)
            return
        }
        setRowContextMenu({ mouseX: event.clientX, mouseY: event.clientY, appointment })
    }, [onRowContextMenu])

    const csvData = React.useMemo(() => {
        if (!showExportCsv) return null;

        const isVisible = (col: ColumnName) =>
            columns[col] && !settings?.hideColumns?.includes(col);

        const getPaymentText = (a: AppointmentData): string => {
            const totalPrice = getTotalPrice(a)
            const totalPaid = getAppointmentTotalPayments(a)
            if (a.packId) return `${t(TK.pack)}/${t(TK.subscription)}`
            if (totalPrice > totalPaid) return t(TK.notPaid)
            const types = [...new Set(a.payments?.filter(p => p.status === PaymentStatus.Manual).map(p => p.type) ?? [])]
            return types.length ? `${t(TK.paid)} (${types.join(', ')})` : t(TK.paid)
        }

        const getConfirmationText = (a: AppointmentData): string => {
            if (!confirmationFeatureAvailable) return ''
            const status = getAppointmentConfirmationStatus(a)
            if (status === 'confirmed') return t(TK.clientConfirmedAppointment)
            if (status === 'rejected') return t(TK.clientRejectedAppointment)
            if (status === 'pending') return t(TK.clientAwaitingConfirmation)
            return ''
        }

        const headers: { label: string; key: string }[] = [
            ...(isVisible('dateTime') ? [{ label: t(settings?.hideDay ? TK.time : TK.dateTime), key: 'dateTime' }] : []),
            ...(confirmationFeatureAvailable && isVisible('confirmation') ? [{ label: t(TK.confirmation), key: 'confirmation' }] : []),
            ...(isVisible('client') ? [{ label: t(TK.client), key: 'client' }] : []),
            ...(isVisible('client') && useClientFileNumber ? [{ label: t(TK.clientFileNumberLabel), key: 'clientFileNumber' }] : []),
            ...(isVisible('professional') ? [{ label: t(TK.professional), key: 'professional' }] : []),
            ...(isVisible('room') ? [{ label: t(TK.room), key: 'room' }] : []),
            ...(isVisible('appointmentType') ? [{ label: t(TK.appointmentType), key: 'appointmentType' }] : []),
            ...(isVisible('extras') ? [{ label: t(TK.extras), key: 'extras' }] : []),
            ...(isVisible('pack') ? [{ label: `${t(TK.pack)}/${t(TK.subscription)}`, key: 'pack' }] : []),
            ...(canShowPrices && isVisible('price') ? [{ label: t(TK.price), key: 'price' }] : []),
            ...(canShowPrices && isVisible('effectivePrice') ? [{ label: t(TK.effectivePrice), key: 'effectivePrice' }] : []),
            ...(canShowPrices && isVisible('payment') ? [{ label: t(TK.payment), key: 'payment' }] : []),
            ...(isVisible('notes') ? [{ label: t(TK.notes), key: 'notes' }] : []),
        ]

        const data = appointments.map(a => ({
            dateTime: settings?.hideDay ? a.start : `${a.date} ${a.start}`,
            confirmation: getConfirmationText(a),
            client: a.clientName ?? '',
            clientFileNumber: a.clientFileNumber != null ? String(a.clientFileNumber) : '',
            professional: a.professionalName ?? '',
            room: a.room ?? '',
            appointmentType: serviceName(a.type, generalSettings),
            extras: a.expenses?.map(e => e.name).join(' + ') ?? '',
            pack: formatPackInfo(a),
            price: canShowPrices ? formatEuros(getTotalPrice(a)) : '',
            effectivePrice: canShowPrices ? formatEuros(a.actualPriceWithPack) : '',
            payment: canShowPrices ? getPaymentText(a) : '',
            notes: a.notes ?? '',
        }))

        return { headers, data }
    }, [showExportCsv, appointments, columns, settings, t, canShowPrices, confirmationFeatureAvailable, generalSettings, useClientFileNumber])

    const paymentMenuMissingValue = React.useMemo(() => {
        if (!paymentMenuState?.appointment) {
            return 0
        }

        const totalToPay = getTotalPrice(paymentMenuState.appointment) * (1 + (paymentMenuState.appointment.vat ?? 0))
        const totalPaid = getAppointmentTotalPayments(paymentMenuState.appointment)
        return Math.max(0, totalToPay - totalPaid)
    }, [paymentMenuState])

    return (<>
        <div className="topButtonsContainer">
            <IconButton id='table-print-button' size='small' onClick={handlePrint}>
                <PrintIcon/>
            </IconButton>
            { canEdit && onCreateAppointment &&
                <AppButton id="create-appointment-button" style={{ marginLeft: 'var(--spacer-S)' }} onClick={onCreateAppointment}>{t(TK.create)}</AppButton>
            }
        </div>
        <div className={classnames("appointmentsTable", className)}>

            <StyledAppointmentsTable id="appointments-table" scrollable hover>
                <thead>
                    <tr>
                        { columns['dateTime'] && !settings?.hideColumns?.includes('dateTime') && <th className="timeHeader">{t(settings?.hideDay ? TK.time : TK.dateTime)}</th> }
                        { confirmationFeatureAvailable && columns['confirmation'] && !settings?.hideColumns?.includes('confirmation') && (
                            <th className="confirmationHeader"/>
                        ) }
                        { columns['client'] && !settings?.hideColumns?.includes('client') && <th className="clientHeader">{t(TK.client)}</th> }
                        { columns['professional'] && !settings?.hideColumns?.includes('professional') && <th className="professionalHeader">{t(TK.professional)}</th> }
                        { columns['room'] && !settings?.hideColumns?.includes('room') && <th className="roomHeader">{t(TK.room)}</th> }
                        { columns['appointmentType'] && !settings?.hideColumns?.includes('appointmentType') && <th className="typeHeader">{t(TK.appointmentType)}</th> }
                        { columns['extras'] && !settings?.hideColumns?.includes('extras') && <th className="extrasHeader"></th> }
                        { columns['pack'] && !settings?.hideColumns?.includes('pack') && <th className="packHeader">{t(TK.pack)}/{t(TK.subscription)}</th> }
                        { canShowPrices && columns['price'] && !settings?.hideColumns?.includes('price') && <th className="priceHeader">{t(TK.price)}</th> }
                        { canShowPrices && columns['effectivePrice'] && !settings?.hideColumns?.includes('effectivePrice') && <th className="effectivePriceHeader">{t(TK.effectivePrice)}</th> }
                        { canShowPrices && columns['payment'] && !settings?.hideColumns?.includes('payment') && <th/> }
                        { columns['actionButtons'] && !settings?.hideColumns?.includes('actionButtons') && <th/> }
                        { columns['notes'] && !settings?.hideColumns?.includes('notes') && <th className="notesHeader">{t(TK.notes)}</th> }
                        <th style={{padding: 0}}>
                            <div className="tableHeaderActions">
                                <IconButton id='table-full-width-button' size='small' onClick={pageWidth.toggleFullWidth}>
                                    {pageWidth.fullWidth ? <UnfoldLessIcon className="tableFullWidthIcon"/> : <UnfoldMoreIcon className="tableFullWidthIcon"/>}
                                </IconButton>
                                <IconButton id='table-settings-button' size='small' onClick={() => setColumnsModalOpen(true)}>
                                    <SettingsIcon/>
                                </IconButton>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {
                        appointments.map((appointment, idx) => {
                            const confirmationStatus = confirmationFeatureAvailable
                                ? getAppointmentConfirmationStatus(appointment)
                                : null;

                            const confirmationTitle = confirmationStatus === 'confirmed'
                                ? t(TK.clientConfirmedAppointment)
                                : confirmationStatus === 'rejected'
                                    ? t(TK.clientRejectedAppointment)
                                    : confirmationStatus === 'pending'
                                        ? t(TK.clientAwaitingConfirmation)
                                        : undefined;
                            const confirmationTooltipId = `appointment-${idx}-confirmation-status`;
                            const confirmationDate = confirmationStatus === 'confirmed'
                                ? appointment.confirmedByClientAt
                                : confirmationStatus === 'rejected'
                                    ? appointment.deniedByClientAt
                                    : getConfirmationRequestSentAt(appointment);
                            const confirmationDateLabel = formatDateTime(confirmationDate);
                            const confirmationToolTipContent = confirmationStatus
                                ? (
                                    <div>
                                        <div>{confirmationTitle}</div>
                                        {confirmationDateLabel && <div>{t(TK.date)}: {confirmationDateLabel}</div>}
                                    </div>
                                )
                                : null;
                            const confirmationIndicator = confirmationStatus
                                ? (
                                    <span
                                        id={confirmationTooltipId}
                                        className={classnames('confirmation-indicator', confirmationStatus)}
                                        onClick={canConfirmAppointments && onSetAppointmentConfirmationStatus ? handleOpenConfirmationMenu(appointment) : undefined}
                                        role={canConfirmAppointments && onSetAppointmentConfirmationStatus ? 'button' : undefined}
                                        tabIndex={canConfirmAppointments && onSetAppointmentConfirmationStatus ? 0 : undefined}
                                        style={canConfirmAppointments && onSetAppointmentConfirmationStatus ? { cursor: 'pointer' } : undefined}
                                    >
                                        {confirmationStatus === 'confirmed' && <MobileFriendlyIcon fontSize="small" />}
                                        {confirmationStatus === 'rejected' && <PhonelinkEraseIcon fontSize="small" />}
                                        {confirmationStatus === 'pending' && <DeviceUnknownIcon fontSize="small" />}
                                    </span>
                                )
                                : null;

                            return (
                                <tr id={`appointment-${idx}`} key={appointment.id} className={onEditAppointment ? 'selectable' : undefined} onContextMenu={handleRowContextMenu(appointment)}>
                                    { columns['dateTime'] && !settings?.hideColumns?.includes('dateTime') &&
                                        <td id="date-time-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            <PrimaryText>{`${settings?.hideDay ? '' : `${appointment.date} | `} ${appointment.start}`}</PrimaryText>
                                        </td>
                                    }
                                    { confirmationFeatureAvailable && columns['confirmation'] && !settings?.hideColumns?.includes('confirmation') &&
                                        <td id="confirmation-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {confirmationIndicator}
                                            {confirmationToolTipContent && (
                                                <ToolTip target={confirmationTooltipId} toolTipContent={confirmationToolTipContent} />
                                            )}
                                        </td>
                                    }
                                    { columns['client'] && !settings?.hideColumns?.includes('client') &&
                                        <td id="client-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {onOpenClient && appointment.clientId
                                                ? <span className="entityNameLink" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpenClient(appointment.clientId!); }}>
                                                    <PrimaryText>{shortenName(appointment.clientName ?? '')}</PrimaryText>
                                                  </span>
                                                : <PrimaryText>{shortenName(appointment.clientName ?? '')}</PrimaryText>
                                            }
                                            {useClientFileNumber && appointment.clientFileNumber != null &&
                                                <><br/><SecondaryText>{getClientFileNumberLabel(appointment.clientFileNumber, t(TK.clientFileNumberLabel))}</SecondaryText></>
                                            }
                                            {(() => {
                                                const warningNote = appointment.clientId ? clientsById[appointment.clientId]?.warningNote : undefined
                                                return warningNote
                                                    ? <><br/><span style={{ color: 'var(--danger-color)', fontSize: '0.8em', display: 'flex', alignItems: 'center', gap: '2px' }}><WarningIcon style={{ fontSize: '0.9em' }} />{warningNote}</span></>
                                                    : null
                                            })()}
                                        </td>
                                    }
                                    { columns['professional'] && !settings?.hideColumns?.includes('professional') &&
                                        <td id="professional-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            <ColorBall color={appointment.professionalColor}>{appointment.professionalName}</ColorBall>
                                        </td>
                                    }
                                    { columns['room'] && !settings?.hideColumns?.includes('room') &&
                                        <td id="room-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            <SecondaryText>{appointment.room}</SecondaryText>
                                        </td>
                                    }
                                    { columns['appointmentType'] && !settings?.hideColumns?.includes('appointmentType') &&
                                        <td id="type-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {generalSettings.services[appointment.type]?.color
                                                ? <ColorBall color={generalSettings.services[appointment.type]?.color}>{serviceName(appointment.type, generalSettings)}</ColorBall>
                                                : serviceName(appointment.type, generalSettings)
                                            }
                                            {(appointment.groupClients?.length ?? 0) > 0 && ` (${appointment.groupClients!.length})`}
                                        </td>
                                    }
                                    { columns['extras'] && !settings?.hideColumns?.includes('extras') &&
                                        <td id="type-extras-cell" onClick={handleOpenAppointment(appointment)}>
                                            {appointment.expenses.map(e => e.name).join(' + ')}
                                        </td>
                                    }
                                    { columns['pack'] && !settings?.hideColumns?.includes('pack') &&
                                        <td id="pack-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {onOpenPack && appointment.packId
                                                ? <span className="entityNameLink" onClick={(e: React.MouseEvent) => { e.stopPropagation(); onOpenPack(appointment.packId!); }}>
                                                    <PrimaryText>{formatPackInfo(appointment)}</PrimaryText>
                                                  </span>
                                                : formatPackInfo(appointment)
                                            }
                                        </td>
                                    }
                                    { canShowPrices && columns['price'] && !settings?.hideColumns?.includes('price') &&
                                        <td id="price-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {formatEuros(getTotalPrice(appointment))}
                                        </td>
                                    }
                                    { canShowPrices && columns['effectivePrice'] && !settings?.hideColumns?.includes('effectivePrice') &&
                                        <td id="effective-price-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {formatEuros(appointment.actualPriceWithPack)}
                                        </td>
                                    }
                                    { canShowPrices && columns['payment'] && !settings?.hideColumns?.includes('payment') &&
                                        <td id="payment-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            {formatPayment(t, appointment)}
                                        </td>
                                    }
                                    { columns['actionButtons'] && !settings?.hideColumns?.includes('actionButtons') && <td>
                                        <div className="rowButtonsContainer">
                                            {!generalSettings.doNotUseAppointmentConfirmation && onMarkAppointmentDone && canConfirmAppointments && (
                                                <Tooltip title={appointment.hasHappened === true ? t(TK.confirmed) : appointment.hasHappened === false ? t(TK.appointmentNotHappened) : t(TK.confirm)}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleMarkAsDone(appointment)}
                                                            style={{ color: appointment.hasHappened === true ? 'var(--success-color)' : appointment.hasHappened === false ? 'var(--danger-color)' : undefined }}
                                                        >
                                                            {appointment.hasHappened === false ? <CancelOutlinedIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {canEdit && canShowPrices && (
                                                <Tooltip title={isPaid(appointment) ? t(TK.paid) : t(TK.notPaid)}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handlePaymentButtonClick(appointment)}
                                                            style={{ color: isPaid(appointment) ? 'var(--success-color)' : 'var(--danger-color)' }}
                                                        >
                                                            <EuroIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td> }
                                    { columns['notes'] && !settings?.hideColumns?.includes('notes') &&
                                        <td id="notes-value-cell" onClick={handleOpenAppointment(appointment)}>
                                            <SecondaryText>{appointment.notes}</SecondaryText>
                                        </td>
                                    }
                                    <td/>
                                </tr>
                            )
                        })
                    }
                    <tr><td colSpan={100} className="loadMoreRow">
                        {
                            isLoading ? <Spinner color="secundary" />
                                : onLoadMore && hasMoreToLoad ? (
                                    <AppButton
                                        color="link"
                                        onClick={() => { setIsLoadingMore(true); onLoadMore(appointments.length); }}
                                    >
                                        {t(TK.loadMore)}
                                    </AppButton>)
                                : <></>
                        }

                    </td></tr>
                </tbody>
            </StyledAppointmentsTable>

            {csvData && (
                <div style={{ marginTop: 'var(--spacer-XS)' }}>
                    <CSVLink
                        headers={csvData.headers}
                        data={csvData.data}
                        separator=';'
                        target='_blank'
                        filename='appointments-export.csv'
                    >
                        {t(TK.exportData)}
                    </CSVLink>
                </div>
            )}

            {onQuickPayment && (
                <Menu
                    id="appointments-table-payment-menu"
                    anchorEl={paymentMenuState?.anchorEl ?? null}
                    open={!!paymentMenuState}
                    onClose={closePaymentMenu}
                >
                    {paymentMenuMissingValue > 0 && generalSettings.paymentMethods.map(method => (
                        <MenuItem key={method} onClick={() => handleQuickPaymentClick(method)}>
                            <PaymentLabel paymentType={method} />
                        </MenuItem>
                    ))}
                    {onEditPayment && (
                        <MenuItem onClick={handleAdvancedPaymentClick}>
                            {t(TK.advanced)}...
                        </MenuItem>
                    )}
                </Menu>
            )}

            <AppointmentConfirmationStatusMenu
                anchorEl={confirmationMenuState?.anchorEl ?? null}
                open={!!confirmationMenuState}
                onClose={handleCloseConfirmationMenu}
                onSelect={handleSetAppointmentConfirmationStatus}
            />

            {rowContextMenu && (
                <AppointmentContextMenu
                    mouseX={rowContextMenu.mouseX}
                    mouseY={rowContextMenu.mouseY}
                    appointment={rowContextMenu.appointment}
                    onClose={() => setRowContextMenu(null)}
                    onConfirm={onMarkAppointmentDone ? () => onMarkAppointmentDone(rowContextMenu.appointment) : undefined}
                    onEdit={onEditAppointment ? () => onEditAppointment(rowContextMenu.appointment) : undefined}
                    onEditPayment={onEditPayment ? () => onEditPayment(rowContextMenu.appointment) : undefined}
                    onQuickPayment={onQuickPayment ? (type) => onQuickPayment(rowContextMenu.appointment, type) : undefined}
                    onCreateInvoice={onCreateInvoice ? () => onCreateInvoice(rowContextMenu.appointment) : undefined}
                    onSendSurvey={onSendSurvey ? () => onSendSurvey(rowContextMenu.appointment) : undefined}
                    canSendSurvey={onSendSurvey ? Boolean(rowContextMenu.appointment.clientId) : undefined}
                    onOpenClient={onOpenClient && rowContextMenu.appointment.clientId ? () => onOpenClient(rowContextMenu.appointment.clientId!) : undefined}
                    onDelete={onDeleteAppointment ? () => onDeleteAppointment(rowContextMenu.appointment) : undefined}
                />
            )}

            <Modal size='sm' isOpen={columnsModalOpen} toggle={() => setColumnsModalOpen(false)}>
                <ModalHeader toggle={() => setColumnsModalOpen(false)}>{t(TK.settings)} - {t(TK.table)}</ModalHeader>
                    <ModalBody style={{marginLeft: 'var(--spacer-S)'}}>
                        <Typography block style={{marginBottom: 'var(--spacer-XS)'}}><b>{t(TK.columns)}</b></Typography>
                        <div style={{marginLeft: 'var(--spacer-S)', display: 'flex', flexDirection: 'column'}}>
                            {allColumns
                                .filter(column => !settings?.hideColumns?.includes(column))
                                .filter(column => confirmationFeatureAvailable || column !== 'confirmation')
                                .filter(column => canShowPrices || !(['price', 'effectivePrice', 'payment'] as ColumnName[]).includes(column))
                                .map(column => (
                                <CheckInputField
                                    value={columns[column]}
                                    onChange={value => setColumns({...columns, [column]: value})}
                                    key={column}
                                    label={t(column as TK)}
                                />
                            ))}
                        </div>
                </ModalBody>
            </Modal>
        </div>
    </>);
}

export default AppointmentsTable;
